import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAllowedOrigins } from '@/api/(admin)/allowed-origins';

export interface OriginsQueryInput {
  page: number;
  limit: number;
  sort?: string | undefined;
  filters: Record<string, string[]>;
}

/** Every filter the list endpoint accepts; anything else is dropped. */
const FILTER_KEYS = new Set(['companyName', 'name', 'prettyOrigin', 'origin']);

/**
 * The table's filter state as the endpoint's `filter` JSON.
 *
 * One value per column — `useTable` holds arrays because its pills are
 * multi-select, but this endpoint matches a single term per field.
 */
function buildFilterJson(filters: Record<string, string[]>): string | null {
  const out: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters)) {
    const first = values?.[0];
    if (FILTER_KEYS.has(key) && first !== undefined && first !== '') {
      out[key] = first;
    }
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out) : null;
}

/** The list is 0-indexed; the table counts from 1. */
export function buildOriginsQs(input: OriginsQueryInput): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, input.page - 1)));
  qs.set('count', String(input.limit));
  if (input.sort) qs.set('sort', input.sort);
  const filter = buildFilterJson(input.filters);
  if (filter) qs.set('filter', filter);
  return qs.toString();
}

/** Prefix for every allowed-origins cache entry. */
export const adminOriginsKey = ['admin', 'allowed-origins'] as const;

/** Shared by the route loader and the page hook, keyed on the built query string. */
export function adminOriginsQueryOptions(input: OriginsQueryInput) {
  const qs = buildOriginsQs(input);
  return queryOptions({
    queryKey: [...adminOriginsKey, 'list', qs] as const,
    queryFn: () => fetchAllowedOrigins(qs),
    staleTime: 30_000,
  });
}

export function useAdminOrigins(input: OriginsQueryInput) {
  const query = useQuery(adminOriginsQueryOptions(input));

  return {
    ...query,
    rows: query.data?.data ?? [],
    totalCount: query.data?.metadata.pagination.total ?? 0,
  };
}
